import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    Chip,
    Collapse,
    FormControlLabel,
    IconButton,
    Paper,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faCaretRight, faCircle, faLocationDot, faPlus, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { Standort, StandortTyp } from 'apiClient/Api';
import { RequirementGroup, RequirementImage, RequirementItem } from './calculatorApi';
import { Instance, InstanceMap } from './instanceModel';
import { OptionsInput } from './OptionsInput';
import { CalcImageData, ImageThumb } from './CalcImage';

export interface TreeMutators {
    addInstance: (reqId: string, area: string) => void;
    removeInstance: (reqId: string, iid: number) => void;
    updateInstance: (reqId: string, iid: number, patch: Partial<Instance>) => void;
    updateOpt: (reqId: string, iid: number, fn: (prev: Record<string, number>) => Record<string, number>) => void;
    ensureInstances: (reqIds: string[], area: string) => void;
}

interface TreeProps extends TreeMutators {
    tree: RequirementGroup;
    instances: InstanceMap;
    standorte: Standort[];
    activeStandort: string;
    onSelectStandort: (id: string) => void;
    reqOrder: 'xml' | 'name';
}

interface Domain {
    key: string;
    title: string;
    group: RequirementGroup | null;
    items: RequirementItem[];
    reqIds: string[];
}

const toImageData = (images: RequirementImage[]): CalcImageData[] =>
    (images || []).map((i) => ({
        articleNumber: i.articleNumber,
        name: i.name,
        titel: i.titel,
        beschreibung: i.beschreibung,
        haupt: i.haupt,
        crop: i.crop,
    }));

const orderItems = (items: RequirementItem[], reqOrder: string): RequirementItem[] =>
    reqOrder !== 'name'
        ? items
        : [...items].sort(
              (a, b) =>
                  (a.description || a.id).localeCompare(b.description || b.id, 'de', { sensitivity: 'base' }) ||
                  a.id.localeCompare(b.id),
          );

const orderGroups = (groups: RequirementGroup[], reqOrder: string): RequirementGroup[] =>
    reqOrder !== 'name'
        ? groups
        : [...groups].sort((a, b) => (a.title || '').localeCompare(b.title || '', 'de', { sensitivity: 'base' }));

const reqIdsInGroup = (g: RequirementGroup): string[] => [
    ...g.items.filter((i) => !i.implicit).map((i) => i.id),
    ...g.groups.flatMap(reqIdsInGroup),
];

const ConfiguredDot: React.FC = () => (
    <FontAwesomeIcon icon={faCircle} style={{ fontSize: 8, marginLeft: 6, color: 'var(--mui-palette-success-main, #2e7d32)' }} />
);

/** Eine Instanz (Variante) einer Anforderung am aktuell gewählten Standort. */
const InstanceRow: React.FC<{
    item: RequirementItem;
    inst: Instance;
    idx: number;
    removable: boolean;
    mutators: TreeMutators;
}> = ({ item, inst, idx, removable, mutators }) => {
    const hasOptions = item.optionGroups && item.optionGroups.length > 0;

    return (
        <Box sx={{ mt: 1, p: 1, borderRadius: 1, bgcolor: 'action.hover' }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField
                    label="Menge"
                    type="number"
                    size="small"
                    value={inst.menge || ''}
                    onChange={(e) => mutators.updateInstance(item.id, inst.iid, { menge: Number(e.target.value) || 0 })}
                    slotProps={{ htmlInput: { min: 0, step: 1, style: { width: 80 } } }}
                />
                {(hasOptions || idx > 0) && (
                    <TextField
                        label="Bezeichnung (optional)"
                        size="small"
                        value={inst.setName}
                        onChange={(e) => mutators.updateInstance(item.id, inst.iid, { setName: e.target.value })}
                        sx={{ flexGrow: 1, minWidth: 160 }}
                    />
                )}
                {removable && (
                    <Tooltip title="Variante entfernen">
                        <IconButton size="small" color="error" onClick={() => mutators.removeInstance(item.id, inst.iid)}>
                            <FontAwesomeIcon icon={faXmark} />
                        </IconButton>
                    </Tooltip>
                )}
            </Stack>
            {hasOptions && inst.menge > 0 && (
                <OptionsInput
                    groups={item.optionGroups}
                    opt={inst.opt}
                    onChange={(fn) => mutators.updateOpt(item.id, inst.iid, fn)}
                />
            )}
        </Box>
    );
};

/** Eine Anforderung mit ihren Instanzen am aktiven Standort. */
const RequirementRow: React.FC<{
    item: RequirementItem;
    instances: Instance[];
    area: string;
    mutators: TreeMutators;
}> = ({ item, instances, area, mutators }) => {
    const configured = instances.some((i) => i.menge > 0);
    return (
        <Box sx={{ mb: 1.5, p: 1, border: (t) => `1px solid ${t.palette.divider}`, borderRadius: 1 }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle2">
                        {item.description || item.id}
                        {configured && <ConfiguredDot />}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {item.id}
                    </Typography>
                </Box>
                <ImageThumb title={item.description || item.id} images={toImageData(item.images)} />
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<FontAwesomeIcon icon={faPlus} />}
                    onClick={() => mutators.addInstance(item.id, area)}
                >
                    Variante
                </Button>
            </Stack>
            {instances.map((inst, idx) => (
                <InstanceRow
                    key={inst.iid}
                    item={item}
                    inst={inst}
                    idx={idx}
                    removable={instances.length > 1}
                    mutators={mutators}
                />
            ))}
        </Box>
    );
};

/** Rekursive Kategorie (einklappbar) innerhalb einer Domäne – ohne Rubrik-Filter, je Standort. */
const CategoryNode: React.FC<{
    group: RequirementGroup;
    area: string;
    instancesOf: (reqId: string) => Instance[];
    configuredReq: (reqId: string) => boolean;
    applies: (item: RequirementItem) => boolean;
    reqOrder: string;
    mutators: TreeMutators;
    depth: number;
}> = ({ group, area, instancesOf, configuredReq, applies, reqOrder, mutators, depth }) => {
    const [open, setOpen] = useState(depth === 0);
    const items = orderItems(group.items.filter((i) => !i.implicit && applies(i)), reqOrder);
    const subGroups = orderGroups(group.groups, reqOrder);
    const hasConfigured = reqIdsInGroup(group).some(configuredReq);

    // Untergruppe ausblenden, wenn sie für diesen Standort-Typ nichts Wählbares enthält.
    const subVisible = (g: RequirementGroup): boolean =>
        g.items.some((i) => !i.implicit && applies(i)) || g.groups.some(subVisible);
    const visibleSubGroups = subGroups.filter(subVisible);
    if (items.length === 0 && visibleSubGroups.length === 0) return null;

    return (
        <Box sx={{ mb: 1 }}>
            <Stack
                direction="row"
                spacing={1}
                onClick={() => setOpen((o) => !o)}
                sx={{ alignItems: 'center', cursor: 'pointer', py: 0.5, px: 1, borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}
            >
                <FontAwesomeIcon icon={open ? faCaretDown : faCaretRight} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {group.title || '(ohne Titel)'}
                </Typography>
                {hasConfigured && <ConfiguredDot />}
            </Stack>
            <Collapse in={open} timeout="auto" unmountOnExit>
                <Box sx={{ pl: 2, pt: 1 }}>
                    {items.map((item) => (
                        <RequirementRow key={item.id} item={item} instances={instancesOf(item.id)} area={area} mutators={mutators} />
                    ))}
                    {visibleSubGroups.map((sub) => (
                        <CategoryNode
                            key={sub.path}
                            group={sub}
                            area={area}
                            instancesOf={instancesOf}
                            configuredReq={configuredReq}
                            applies={applies}
                            reqOrder={reqOrder}
                            mutators={mutators}
                            depth={depth + 1}
                        />
                    ))}
                </Box>
            </Collapse>
        </Box>
    );
};

export const RequirementInputTree: React.FC<TreeProps> = (props) => {
    const { tree, instances, standorte, activeStandort, onSelectStandort, reqOrder } = props;
    const mutators: TreeMutators = props;

    const domains = useMemo<Domain[]>(() => {
        const list: Domain[] = [];
        const rootItems = tree.items.filter((i) => !i.implicit);
        if (rootItems.length) {
            list.push({ key: '__root', title: 'Allgemein', group: null, items: rootItems, reqIds: rootItems.map((i) => i.id) });
        }
        for (const g of tree.groups) {
            list.push({ key: g.path, title: g.title || '(ohne Titel)', group: g, items: [], reqIds: reqIdsInGroup(g) });
        }
        return list;
    }, [tree]);

    // Schnelle Auflösung Anforderung → Definition (für availableFor-Filter).
    const itemById = useMemo(() => {
        const map = new Map<string, RequirementItem>();
        const walk = (g: RequirementGroup) => {
            for (const i of g.items) map.set(i.id, i);
            for (const s of g.groups) walk(s);
        };
        walk(tree);
        return map;
    }, [tree]);

    // Standort-Typ-Filter: Anforderung wählbar, wenn availableFor leer/fehlt oder den aktiven Typ enthält.
    const activeTyp = standorte.find((s) => s.id === activeStandort)?.typ ?? StandortTyp.StandAlone;
    const applies = (item: RequirementItem): boolean => !item.availableFor?.length || item.availableFor.includes(activeTyp);
    const appliesId = (reqId: string): boolean => {
        const it = itemById.get(reqId);
        return !it || applies(it);
    };

    // Instanzen des aktiven Standorts.
    const instancesOf = (reqId: string): Instance[] =>
        (instances[reqId] || []).filter((i) => (i.area || 'A') === activeStandort);
    const configuredReq = (reqId: string): boolean => instancesOf(reqId).some((i) => i.menge > 0);

    // Nur Domänen mit – für diesen Standort-Typ – wählbaren Anforderungen.
    const visibleDomains = domains.filter((d) => d.reqIds.some(appliesId));

    // Auto-aktiv: Domänen, die am aktiven Standort schon konfigurierte Instanzen haben.
    const autoActive = (d: Domain): boolean => d.reqIds.some((id) => configuredReq(id));

    // Manuelle An/Aus-Schalter je Standort.
    const [manual, setManual] = useState<Record<string, Record<string, boolean>>>({});
    const isEnabled = (d: Domain): boolean => manual[activeStandort]?.[d.key] ?? autoActive(d);
    const enabledDomains = visibleDomains.filter(isEnabled);

    const setEnabled = (d: Domain, on: boolean) => {
        setManual((prev) => ({ ...prev, [activeStandort]: { ...(prev[activeStandort] || {}), [d.key]: on } }));
        if (on) mutators.ensureInstances(d.reqIds.filter(appliesId), activeStandort);
    };

    // Beim Aktivieren/Standortwechsel sicherstellen, dass die Eingabefelder existieren (nur wählbare).
    const ensureKey = enabledDomains.map((d) => d.key).sort().join('|');
    useEffect(() => {
        const ids = enabledDomains.flatMap((d) => d.reqIds.filter(appliesId));
        if (ids.length) mutators.ensureInstances(ids, activeStandort);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStandort, ensureKey, activeTyp]);

    const renderDomainContent = (d: Domain) => (
        <Box sx={{ mt: 1 }}>
            {orderItems(d.items.filter(applies), reqOrder).map((item) => (
                <RequirementRow key={item.id} item={item} instances={instancesOf(item.id)} area={activeStandort} mutators={mutators} />
            ))}
            {d.group &&
                orderItems(d.group.items.filter((i) => !i.implicit && applies(i)), reqOrder).map((item) => (
                    <RequirementRow key={item.id} item={item} instances={instancesOf(item.id)} area={activeStandort} mutators={mutators} />
                ))}
            {d.group &&
                orderGroups(d.group.groups, reqOrder).map((sub) => (
                    <CategoryNode
                        key={sub.path}
                        group={sub}
                        area={activeStandort}
                        instancesOf={instancesOf}
                        configuredReq={configuredReq}
                        applies={applies}
                        reqOrder={reqOrder}
                        mutators={mutators}
                        depth={0}
                    />
                ))}
        </Box>
    );

    const activeName = standorte.find((s) => s.id === activeStandort)?.name || activeStandort;

    return (
        <Box>
            {/* Standort-Auswahl */}
            {standorte.length > 1 && (
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
                    <FontAwesomeIcon icon={faLocationDot} />
                    <Typography variant="body2" color="text.secondary">
                        Konfiguration für Standort:
                    </Typography>
                    <ToggleButtonGroup
                        exclusive
                        size="small"
                        color="primary"
                        value={activeStandort}
                        onChange={(_, v) => v && onSelectStandort(v)}
                    >
                        {standorte.map((s) => (
                            <ToggleButton key={s.id} value={s.id!}>
                                {s.name || s.id}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                </Stack>
            )}

            {/* Domänen-Auswahl (opt-in) */}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Welche Bereiche möchtest du für „{activeName}" konfigurieren?
            </Typography>
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', mb: 2 }} useFlexGap>
                {visibleDomains.map((d) => (
                    <FormControlLabel
                        key={d.key}
                        control={<Checkbox checked={isEnabled(d)} onChange={(e) => setEnabled(d, e.target.checked)} />}
                        label={
                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                {d.title}
                                {d.reqIds.some(configuredReq) && <ConfiguredDot />}
                            </Box>
                        }
                        sx={{ border: (t) => `1px solid ${t.palette.divider}`, borderRadius: 1, pr: 1, m: 0 }}
                    />
                ))}
            </Stack>

            {/* Konfiguration je aktivierter Domäne */}
            {enabledDomains.length === 0 ? (
                <Typography color="text.secondary">Wähle oben mindestens einen Bereich aus.</Typography>
            ) : (
                enabledDomains.map((d) => (
                    <Paper key={d.key} variant="outlined" sx={{ p: 2, mb: 2 }}>
                        <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="h6">{d.title}</Typography>
                            <Button size="small" color="inherit" onClick={() => setEnabled(d, false)}>
                                Bereich ausblenden
                            </Button>
                        </Stack>
                        {renderDomainContent(d)}
                    </Paper>
                ))
            )}
        </Box>
    );
};
